import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import { API_URL } from "../../constant";

const PDF_SRC = "/documents/parental_consent_form.pdf";
const DOCX_SRC = "/documents/ParentalConsent.docx";

const resolveSignatureImageUrl = (url) => {
  if (!url) {
    return "";
  }

  const apiBase = API_URL.replace(/\/+$/, "");

  try {
    const parsed = new URL(url);
    const apiParsed = new URL(apiBase);
    const isLocalHost = (hostname) => hostname === "localhost" || hostname === "127.0.0.1";

    if (!parsed.port && apiParsed.port) {
      if (
        parsed.hostname === apiParsed.hostname ||
        (isLocalHost(parsed.hostname) && isLocalHost(apiParsed.hostname))
      ) {
        return `${parsed.protocol}//${parsed.hostname}:${apiParsed.port}${parsed.pathname}${parsed.search}`;
      }
    }

    return url;
  } catch (err) {
    // If the URL is relative, use the API base URL.
    if (url.startsWith("/")) {
      return `${apiBase}${url}`;
    }
    return `${apiBase}/${url}`;
  }
};

const ParentsConsentContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sigRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [consent, setConsent] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    child_full_name: "",
    date_of_birth: "",
    parent_guardian_name: "",
    relationship_to_child: "",
    consent_date: new Date().toISOString().slice(0, 10),
    email: "",
  });
  const isConsentGateActive =
    localStorage.getItem("requires_parental_consent") === "true";

  const loadConsent = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/parents-consent`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem("auth_token");
        navigate("/sign_in");
        return;
      }

      if (res.status === 404) {
        setConsent(null);
        return;
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Could not load consent status.");
      }

      const data = await res.json();
      if (data.review_status === "approved") {
        localStorage.removeItem("requires_parental_consent");
      }
      setConsent(data);
    } catch (e) {
      toast.error(e.message || "Something went wrong.");
      setConsent(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  useEffect(() => {
    if (consent && consent.review_status === "rejected") {
      setForm((prev) => ({
        ...prev,
        child_full_name: consent.child_full_name || "",
        date_of_birth: consent.date_of_birth || "",
        parent_guardian_name: consent.parent_guardian_name || "",
        relationship_to_child: consent.relationship_to_child || "",
        consent_date: new Date().toISOString().slice(0, 10),
        email: consent.email || "",
      }));
    }
  }, [consent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please draw your signature.");
      return;
    }

    setSubmitting(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        try {
          sigRef.current.getCanvas().toBlob((b) => resolve(b), "image/png", 1.0);
        } catch (err) {
          reject(err);
        }
      });

      if (!blob) {
        throw new Error("Could not capture signature.");
      }

      const body = new FormData();
      body.append("child_full_name", form.child_full_name.trim());
      body.append("date_of_birth", form.date_of_birth);
      body.append("parent_guardian_name", form.parent_guardian_name.trim());
      body.append("relationship_to_child", form.relationship_to_child.trim());
      body.append("consent_date", form.consent_date);
      if (form.email.trim()) {
        body.append("email", form.email.trim());
      }
      body.append("signature", blob, "signature.png");

      const res = await fetch(`${API_URL}/api/parents-consent`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        localStorage.removeItem("auth_token");
        navigate("/sign_in");
        return;
      }

      if (res.status === 409) {
        toast.error(data.message || "Consent was already submitted.");
        await loadConsent();
        return;
      }

      if (!res.ok) {
        const msg =
          data.message ||
          (data.errors && Object.values(data.errors).flat().join(" ")) ||
          "Submission failed.";
        throw new Error(msg);
      }

      toast.success("Parental consent submitted.");
      localStorage.removeItem("requires_parental_consent");
      setConsent(data);
      if (isConsentGateActive) {
        setTimeout(() => {
          navigate("/customer/wallet");
        }, 8000);
      }
    } catch (err) {
      toast.error(err.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="ico_about_section section_space section_decoration bg-white">
        <div className="container">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (consent && typeof consent === "object" && consent.id && consent.review_status !== "rejected") {
    return (
      <section className="ico_about_section section_space section_decoration bg-white">
        <div className="container">
          <div className="row justify-content-lg-between">
            <div className="col-lg-12">
              <h5 className="title_text text-dark">Submitted consent</h5>
              <p className="text-dark opacity-75">
                This account already has a parental / guardian consent on file.
                Review status: <strong>{consent.review_status || "pending"}</strong>.
              </p>
              {consent.review_status === "rejected" ? (
                <div className="alert alert-danger">
                  This consent was rejected by an administrator. Please submit a
                  new form with corrected information.
                </div>
              ) : null}
              {consent.review_status === "pending" ? (
                <div className="alert alert-warning">
                  This consent is pending superadmin review.
                </div>
              ) : null}
              {consent.review_status === "approved" ? (
                <div className="alert alert-success">
                  Consent has been approved. Full account access is enabled.
                </div>
              ) : null}
              <dl className="row text-dark opacity-75">
                <dt className="col-sm-3">Child&apos;s full name</dt>
                <dd className="col-sm-9">{consent.child_full_name}</dd>
                <dt className="col-sm-3">Date of birth</dt>
                <dd className="col-sm-9">{consent.date_of_birth}</dd>
                <dt className="col-sm-3">Parent / guardian</dt>
                <dd className="col-sm-9">{consent.parent_guardian_name}</dd>
                <dt className="col-sm-3">Relationship</dt>
                <dd className="col-sm-9">{consent.relationship_to_child}</dd>
                <dt className="col-sm-3">Consent date</dt>
                <dd className="col-sm-9">{consent.consent_date}</dd>
                {consent.email ? (
                  <>
                    <dt className="col-sm-3">Email</dt>
                    <dd className="col-sm-9">{consent.email}</dd>
                  </>
                ) : null}
                {consent.review_notes ? (
                  <>
                    <dt className="col-sm-3">Reviewer notes</dt>
                    <dd className="col-sm-9">{consent.review_notes}</dd>
                  </>
                ) : null}
                <dt className="col-sm-3">Signature</dt>
                <dd className="col-sm-9">
                  {consent.signature_image_url ? (
                    <img
                      src={resolveSignatureImageUrl(consent.signature_image_url)}
                      alt="Signature"
                      className="img-fluid border rounded bg-white p-2"
                      style={{ maxHeight: "160px" }}
                    />
                  ) : null}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="ico_about_section section_space section_decoration bg-white">
      <div className="container">
        <div className="row justify-content-lg-between">
          <div className="col-lg-12">
            <h5 className="title_text text-dark">
              Parental / Guardian Consent Form
            </h5>
            {(isConsentGateActive || location.state?.fromUnder18Signup || location.state?.gated) ? (
              <div className="alert alert-warning mt-3">
                Your account is marked as under 18. Parent or guardian consent
                is required before full account access is enabled.
              </div>
            ) : null}
            {consent?.review_status === "rejected" ? (
              <div className="alert alert-danger mt-3">
                Your previous submission was rejected.
                {consent.review_notes ? ` ${consent.review_notes}` : ""}
              </div>
            ) : null}
            <p className="text-dark opacity-75">
              <strong>For Users Under 16 — Topcoin UK Participation</strong>
            </p>
            <p className="text-dark opacity-75">
              Topcoin UK is a reward-based promotional platform where users
              receive free units when shopping with participating businesses. This
              is not an investment, cryptocurrency, or gambling service.
            </p>
            <p className="text-dark opacity-75">
              If you are under 16, we require parental or guardian consent for you
              to participate.
            </p>

            <h6 className="title_text text-dark mt-4">Parent / Guardian Consent</h6>
            <p className="text-dark opacity-75">
              I, the undersigned, confirm that I am the parent or legal guardian
              of the child named below. I give permission for my child to sign up
              for and use Topcoin UK.
            </p>
            <p className="text-dark opacity-75">I understand that:</p>
            <ul className="text-dark opacity-75">
              <li>Topcoin UK does not collect or request financial information from users.</li>
              <li>
                Topcoin UK operates a non-investment, non-gambling, and
                non-financial reward system.
              </li>
              <li>
                My child may receive reward units and unlock digital wallets as
                part of the loyalty program.
              </li>
              <li>
                My child&apos;s personal data will be handled according to UK GDPR
                regulations.
              </li>
            </ul>

            {/* <div className="mt-4 mb-3 d-flex flex-wrap gap-2">
              <a
                className="btn btn-outline-primary btn-sm"
                href={PDF_SRC}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open official PDF
              </a>
              <a
                className="btn btn-outline-secondary btn-sm"
                href={DOCX_SRC}
                download
              >
                Download Word (.docx)
              </a>
            </div> */}


            <h5 className="title_text text-dark mt-5">Digital submission</h5>
            <p className="text-dark opacity-75">
              Sign in as the young person&apos;s account, then complete this section
              with the parent or guardian. Rejected submissions can be corrected
              and resubmitted.
            </p>

            <form onSubmit={handleSubmit} className="mt-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="child_full_name">
                    Child&apos;s full name
                  </label>
                  <input
                    id="child_full_name"
                    name="child_full_name"
                    className="form-control"
                    required
                    maxLength={255}
                    value={form.child_full_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="date_of_birth">
                    Date of birth
                  </label>
                  <input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    className="form-control"
                    required
                    value={form.date_of_birth}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="parent_guardian_name">
                    Parent / guardian name
                  </label>
                  <input
                    id="parent_guardian_name"
                    name="parent_guardian_name"
                    className="form-control"
                    required
                    maxLength={255}
                    value={form.parent_guardian_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="relationship_to_child">
                    Relationship to child
                  </label>
                  <input
                    id="relationship_to_child"
                    name="relationship_to_child"
                    className="form-control"
                    required
                    maxLength={100}
                    value={form.relationship_to_child}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="consent_date">
                    Date
                  </label>
                  <input
                    id="consent_date"
                    name="consent_date"
                    type="date"
                    className="form-control"
                    required
                    value={form.consent_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-dark" htmlFor="email">
                    Email (optional)
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="form-control"
                    maxLength={255}
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="form-label text-dark d-block">Signature</label>
                <p className="small text-muted mb-2">
                  Draw the parent / guardian signature in the box.
                </p>
                <div className="border rounded bg-white d-inline-block" style={{ maxWidth: "100%" }}>
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="black"
                    canvasProps={{
                      width: 500,
                      height: 200,
                      className: "signature-canvas d-block",
                      style: { touchAction: "none" },
                    }}
                  />
                </div>
                </div>
                
              <div className="mt-3 d-flex  gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mt-2 px-3 py-2"
                  onClick={() => sigRef.current && sigRef.current.clear()}
                >
                  Clear signature
                </button>
              

              <button
                type="submit"
                className="btn btn-outline-secondary btn-sm mt-2 pconsent-submit-btn px-3  py-2"
                style={{width:"auto"}}
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit consent"}
              </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentsConsentContent;
